import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { useState } from "react"
import { z } from "zod"
import {
  DataTable,
  selectColumn,
  createSortableColumn,
  createCustomColumn,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  TABLE_PAGE_SIZE,
  locationTypeMap,
  useExportMutation,
  EditModal,
  createActionsColumn,
  Button,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"
import {
  LOCATION_STORAGE_KEY,
  useLocationFilters,
} from "@/hooks/filters/useLocationFilters"
import { IndexLocation } from "@/api/types/locations"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { locationQueryKeys } from "@/api/query-keys/locations"
import EditLocationAdministratorInfoForm from "@/components/forms/location/EditLocationAdministratorInfoForm"
import EditLocationGeneralInfoForm from "@/components/forms/location/EditLocationGeneralInfoForm"
import EditClassroomsForm from "@/components/forms/location/EditClassroomsForm"
import AddClassroomModal from "@/components/modals/AddClassroomModal"
import DeleteLocationModal from "@/components/modals/location/DeleteLocationModal"
import UnarchiveLocationModal from "@/components/modals/location/UnarchiveLocationModal"
import { can, hasRole } from "@/utils/can"
import { ArchiveLocationModal } from "@/components/modals/location/ArchiveLocationModal"

export const Route = createFileRoute("/beheer/locaties/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "locations"),
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
})

const DEFAULT_COLUMN_VISIBILITY = {
  classrooms: false,
}

const ALL_COLUMN_IDS = [
  "id",
  "name",
  "street",
  "city",
  "locationType",
  "cost",
  "classrooms",
]

function RouteComponent() {
  const router = useRouter()
  const [isEditGeneralInfoModalOpen, setIsEditGeneralInfoModalOpen] =
    useState(false)
  const [isEditContactInfoModalOpen, setIsEditContactInfoModalOpen] =
    useState(false)
  const [isAddClassroomModalOpen, setIsAddClassroomModalOpen] = useState(false)
  const [isEditClassroomsModalOpen, setIsEditClassroomsModalOpen] =
    useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<IndexLocation | null>(null)

  const searchParams = useSearch({ from: "/beheer/locaties/" })
  const navigate = useNavigate({ from: "/beheer/locaties/" })
  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useLocationFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
    queryKeyMerged,
  } = usePaginatedTableWithUrlState({
    queryKey: [...locationQueryKeys.all, activeFilters, { archived }],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: LOCATION_STORAGE_KEY,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const handleNavigateDetail = (locationId: IndexLocation["id"]) => {
    router.navigate({
      to: "/beheer/locaties/$locationId/gegevens",
      params: { locationId: locationId.toString() },
      search: { from: router.state.location.href },
    })
  }

  // Teachers may only read locations; administrative details (type and cost)
  // and edit actions are not meant for them.
  const isTeacher = hasRole("teacher")
  const canUpdateLocations = can("update", "locations")

  const columns: ColumnDef<IndexLocation>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({ accessorKey: "name", name: "Locatie" }),
    createCustomColumn({
      id: "street",
      name: "Adres",
      cell: ({ row }) => (
        <div>
          {row.original.street} {row.original.houseNumber},{" "}
          {row.original.postalCode}
        </div>
      ),
      accessorKey: "name",
    }),
    createCustomColumn({
      id: "classrooms",
      name: "Lokalen",
      cell: ({ row }) => row.original.classroomCount ?? 0,
      accessorKey: "classroomCount",
    }),
    createSortableColumn({ accessorKey: "city", name: "Plaats" }),
    ...(isTeacher
      ? []
      : ([
          createCustomColumn({
            id: "locationType",
            name: "Soort",
            cell: ({ row }) => (
              <p>{locationTypeMap[row.original.locationType]}</p>
            ),
            accessorKey: "locationType",
          }),
          createCustomColumn({
            id: "cost",
            name: "Kosten",
            cell: ({ row }) =>
              row.original.cost ? <>€{row.original.cost} p/dd</> : "",
            accessorKey: "cost",
          }),
        ] as ColumnDef<IndexLocation>[])),
    createActionsColumn([
      {
        label: "Gegevens wijzigen",
        onClick: (e, location) => {
          e.stopPropagation()
          setActiveRow(location)
          setIsEditGeneralInfoModalOpen(true)
        },
        hidden: !canUpdateLocations,
      },
      {
        label: "Beheerder gegevens wijzigen",
        onClick: (e, location) => {
          e.stopPropagation()
          setActiveRow(location)
          setIsEditContactInfoModalOpen(true)
        },
        hidden: !canUpdateLocations,
      },
      {
        label: "Lokaal toevoegen",
        onClick: (e, location) => {
          e.stopPropagation()
          setActiveRow(location)
          setIsAddClassroomModalOpen(true)
        },
        hidden: !canUpdateLocations,
      },
      {
        label: "Lokalen wijzigen",
        onClick: (e, location) => {
          e.stopPropagation()
          setActiveRow(location)
          setIsEditClassroomsModalOpen(true)
        },
        hidden: !canUpdateLocations,
      },
      {
        label: "Locatie verwijderen",
        onClick: (e, location) => {
          e.stopPropagation()
          setActiveRow(location)
          setIsDeleteOpen(true)
        },
        hidden: !can("delete", "locations"),
      },
      {
        label: "Locatie archiveren",
        onClick: (e, location) => {
          e.stopPropagation()
          setActiveRow(location)
          setIsArchiveOpen(true)
        },
        hidden: !can("update", "locations") || archived,
      },
      {
        label: "Locatie herstellen",
        onClick: (e: React.MouseEvent, location: IndexLocation) => {
          e.stopPropagation()
          setActiveRow(location)
          setIsUnarchiveOpen(true)
        },
        hidden: !archived || !can("update", "locations"),
      },
    ]),
  ]

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  const { mutate: exportLocations } = useExportMutation({
    exportApiPrefix: "locations",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title={archived ? "Locaties archief" : "Locaties"}
        data={data?.data.locations || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateDetail}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportLocations,
              }}
            />
            <TableHeaderActions
              createAction={{
                label: "Nieuwe locatie",
                to: "/beheer/locaties/aanmaken",
                entity: "locations",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {activeRow && (
        <EditModal
          isOpen={isEditGeneralInfoModalOpen}
          setIsOpen={setIsEditGeneralInfoModalOpen}
          title="Algemene info bewerken"
        >
          <EditLocationGeneralInfoForm
            locationId={activeRow.id}
            onSuccess={() => setIsEditGeneralInfoModalOpen(false)}
            onCancel={() => setIsEditGeneralInfoModalOpen(false)}
          />
        </EditModal>
      )}

      {activeRow && (
        <EditModal
          isOpen={isEditContactInfoModalOpen}
          setIsOpen={setIsEditContactInfoModalOpen}
          title="Beheerder gegevens bewerken"
        >
          <EditLocationAdministratorInfoForm
            locationId={activeRow.id}
            onSuccess={() => setIsEditContactInfoModalOpen(false)}
            onCancel={() => setIsEditContactInfoModalOpen(false)}
          />
        </EditModal>
      )}

      {activeRow && (
        <EditModal
          isOpen={isEditClassroomsModalOpen}
          setIsOpen={setIsEditClassroomsModalOpen}
          title="Lokalen wijzigen"
        >
          <EditClassroomsForm
            locationId={activeRow.id}
            onSuccess={() => setIsEditClassroomsModalOpen(false)}
            onCancel={() => setIsEditClassroomsModalOpen(false)}
          />
        </EditModal>
      )}

      {activeRow && (
        <AddClassroomModal
          location={activeRow}
          isOpen={isAddClassroomModalOpen}
          setIsOpen={setIsAddClassroomModalOpen}
        />
      )}

      {isArchiveOpen && activeRow && (
        <ArchiveLocationModal
          isOpen={isArchiveOpen}
          setIsOpen={setIsArchiveOpen}
          location={activeRow}
        />
      )}

      {isDeleteOpen && activeRow && (
        <DeleteLocationModal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          location={activeRow}
          queryKey={queryKeyMerged}
        />
      )}

      {isUnarchiveOpen && activeRow && (
        <UnarchiveLocationModal
          isOpen={isUnarchiveOpen}
          setIsOpen={setIsUnarchiveOpen}
          location={activeRow}
        />
      )}
    </ScreenWrapper>
  )
}
